import { Dropdown, Space } from "antd";
import Text from "antd/es/typography/Text";
import { routes } from "config/routes";
import { DATE_FORMAT } from "config/settings";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { BsStopCircle } from "react-icons/bs";
import { MdDelete, MdDone } from "react-icons/md";
import { useParams } from "react-router-dom";
import { customToFixed } from "utils/toFixedNumber";
import { formatNumberWithSpaces } from "utils/formatNumberWithSpaces";
import { FaFileExport } from "react-icons/fa6";
export default function useDebitsTableColumns(
  onFinish: (actionType: string, id?: ID) => void,
  directDebitsOptions: LabelValueType[],
) {
  const { t } = useTranslation();
  const urlParams: RouteParams = useParams();

  const handleOnClick = (id: ID) => {
    routes.navigate(`/company/${urlParams?.companyId}/document/edit/${id}`);
  };

  const menu = (record: ListDirectDebitsData) => {
    const items = [
      {
        key: "download",
        label: (
          <span className="dropdown-menu-item">
            <span className="icon-button-round">
              <FaFileExport />
            </span>
            <span className="dropdown-menu-label">{t("Download")}</span>
          </span>
        ),
        onClick: () => {
          window.open(`${record.xmlUrl}&download=1`, "_blank");
        },
      },
      {
        key: "validate",
        label: (
          <span className="dropdown-menu-item">
            <span className="icon-button-round">
              <MdDone />
            </span>
            <span className="dropdown-menu-label">{t("Validate")}</span>
          </span>
        ),
        onClick: () => onFinish("validate", record?.id),
      },
      {
        key: "cancel",
        label: (
          <span className="dropdown-menu-item">
            <span className="icon-button-round">
              <BsStopCircle />
            </span>
            <span className="dropdown-menu-label">{t("Reject/Cancel remaining payments")}</span>
          </span>
        ),
        onClick: () => onFinish("cancel", record?.id),
      },
      {
        key: "delete",
        label: (
          <span className="dropdown-menu-item">
            <span className="icon-button-round">
              <MdDelete />
            </span>
            <span className="dropdown-menu-label">{t("Delete")}</span>
          </span>
        ),
        onClick: () => onFinish("delete", record?.id),
      },
    ];
    return {
      items,
    };
  };

  return [
    {
      key: "reference",
      title: t("Reference"),
      filterType: {
        type: "SearchFilter",
        varName: "reference_Icontains",
      },
    },
    {
      key: "date",
      title: t("Date"),
      filterType: {
        type: "DateFilter",
        maxDate: dayjs().endOf("day"),
        varName: ["date_Gte", "date_Lte"],
      },
      render: (_text: unknown, record: ListDirectDebitsData) => (
        <span>{dayjs(record?.date).format(DATE_FORMAT)}</span>
      ),
    },
    {
      key: "status",
      title: t("Status"),
      dataIndex: ["status", "label"],
      filterType: {
        type: "SelectFilter",
        varName: "status",
        options: directDebitsOptions,
      },
    },
    {
      key: "numberOfPayments",
      title: t("Nº of payments"),
      dataIndex: "numberOfPayments",
    },
    {
      key: "amountValidated",
      title: t("Collected"),
      align: "right",

      render: (_: unknown, record: ListDirectDebitsData) => {
        const decimalPlaces = record?.groups![0]?.currency?.decimalPlaces;
        const currencyCode = record?.groups![0]?.currency?.code;

        return (
          <Space size="small" direction="vertical">
            {record?.amountValidated && (
              <Text>
                {formatNumberWithSpaces(
                  customToFixed(Number(record?.amountValidated), decimalPlaces),
                )}{" "}
                {currencyCode}
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      key: "amount",
      align: "right",
      title: t("To be collected"),
      render: (_: unknown, record: ListDirectDebitsData) => {
        const decimalPlaces = record?.groups![0]?.currency?.decimalPlaces;
        const currencyCode = record?.groups![0]?.currency?.code;

        return (
          <Space size="small" direction="vertical">
            <Text>
              {record?.amount &&
                formatNumberWithSpaces(customToFixed(Number(record?.amount), decimalPlaces))}{" "}
              {currencyCode}
            </Text>
          </Space>
        );
      },
    },
    {
      dataIndex: t("Action"),
      key: "action",
      title: "Action",
      width: "80px",
      align: "center",
      fixed: "right",
      render: (_text: unknown, record: ListDirectDebitsData) => {
        return (
          <Dropdown.Button
            className="dropdown-button-actions"
            size="small"
            onClick={() => {
              handleOnClick(record.id);
            }}
            menu={menu(record)}
          />
        );
      },
    },
  ];
}
